/* eslint-disable react/no-unescaped-entities */

import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, PhoneCall, Phone, MessageCircle, Mail } from "lucide-react";
import { createLead } from "@/lib/api";
import { fbq } from "@/lib/tracking";

const initial = { nom: "", prenom: "", telephone: "", email: "", ville: "", projet: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await createLead({ ...form, source: "contact_form" });
      fbq.lead({ content_name: "Contact Form", content_category: "Formation" });
      toast.success("Demande reçue !", {
        description: "Notre équipe vous rappelle sous 24h ouvrées.",
      });
      setForm(initial);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Une erreur est survenue. Vérifiez vos informations.";
      toast.error("Échec de l'envoi", { description: typeof msg === "string" ? msg : "Vérifiez vos informations." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:border-[#FFD60A] focus:outline-none focus:ring-1 focus:ring-[#FFD60A] placeholder:text-zinc-600 transition-colors";

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#080808] border-y border-zinc-900" id="contact" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Contact</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Être <span className="text-[#FFD60A]">rappelé</span> par notre équipe
            </h2>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
              Décrivez-nous votre projet en quelques lignes. Un membre de l'équipe Horsepower vous rappelle sous <span className="text-white font-semibold">24 heures ouvrées</span> pour répondre à toutes vos questions.
            </p>
            <div className="mt-8 border-l-2 border-[#FFD60A] pl-6 space-y-3">
              <div className="flex items-center gap-3 text-zinc-300">
                <Check size={16} className="text-[#FFD60A]" />
                <span className="text-sm">Réponse personnalisée sous 24h</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Check size={16} className="text-[#FFD60A]" />
                <span className="text-sm">Étude de votre projet sans engagement</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-300">
                <Check size={16} className="text-[#FFD60A]" />
                <span className="text-sm">Conseil sur la formule adaptée</span>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
              <a href="tel:+33671168024" className="bg-[#0a0a0a] hover:bg-[#111] p-4 group transition-colors" data-testid="contact-direct-phone">
                <div className="flex items-center gap-2 text-[#FFD60A] mb-1">
                  <Phone size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">Téléphone</span>
                </div>
                <div className="font-display text-lg text-white tracking-wide group-hover:text-[#FFD60A] transition-colors">06 71 16 80 24</div>
              </a>
              <a href="https://wa.me/33678229256" target="_blank" rel="noreferrer" className="bg-[#0a0a0a] hover:bg-[#111] p-4 group transition-colors" data-testid="contact-direct-whatsapp">
                <div className="flex items-center gap-2 text-[#FFD60A] mb-1">
                  <MessageCircle size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">WhatsApp</span>
                </div>
                <div className="font-display text-lg text-white tracking-wide group-hover:text-[#FFD60A] transition-colors">06 78 22 92 56</div>
              </a>
              <a href="mailto:direction@horsepower-garage.fr" className="bg-[#0a0a0a] hover:bg-[#111] p-4 group transition-colors" data-testid="contact-direct-email">
                <div className="flex items-center gap-2 text-[#FFD60A] mb-1">
                  <Mail size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">Email</span>
                </div>
                <div className="text-sm text-white tracking-wide group-hover:text-[#FFD60A] transition-colors break-all">direction@horsepower-garage.fr</div>
              </a>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="bg-[#0a0a0a] border border-zinc-800 p-8 md:p-10 relative"
            data-testid="lead-capture-form"
          >
            <div className="absolute -top-3 -left-3 w-12 h-12 bg-[#FFD60A] flex items-center justify-center">
              <PhoneCall size={20} className="text-black" />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Nom *</label>
                <input name="nom" required value={form.nom} onChange={handleChange} className={inputCls} placeholder="Dupont" data-testid="lead-input-nom" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Prénom *</label>
                <input name="prenom" required value={form.prenom} onChange={handleChange} className={inputCls} placeholder="Jean" data-testid="lead-input-prenom" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Téléphone *</label>
                <input name="telephone" required value={form.telephone} onChange={handleChange} className={inputCls} placeholder="06 12 34 56 78" data-testid="lead-input-phone" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Email *</label>
                <input type="email" name="email" required value={form.email} onChange={handleChange} className={inputCls} placeholder="jean.dupont@email.com" data-testid="lead-input-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Ville *</label>
                <input name="ville" required value={form.ville} onChange={handleChange} className={inputCls} placeholder="Toulouse" data-testid="lead-input-ville" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Votre projet professionnel *</label>
                <textarea name="projet" required value={form.projet} onChange={handleChange} rows={4} className={inputCls + " resize-none"} placeholder="Je suis mécanicien depuis 5 ans, je souhaite ajouter la reprogrammation à mon activité..." data-testid="lead-input-projet" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-8 w-full flex items-center justify-center gap-3 py-4 bg-[#FFD60A] text-black font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="lead-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Envoi en cours...
                </>
              ) : success ? (
                <>
                  <Check size={18} />
                  Demande envoyée
                </>
              ) : (
                "Être rappelé"
              )}
            </button>

            <p className="mt-4 text-xs text-zinc-500 text-center">
              Vos données restent confidentielles et ne sont jamais revendues.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
